import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import OrchardMap from './OrchardMap';
import LegacyMigrationDashboard from './LegacyMigrationDashboard';
import { syncOrchards } from '../services/orchardService';
import { listGlobalCropTypes, CropTypeNode } from '../services/cropTypeService';
import { db } from '../db';
import { DataTable, Field, FormSection, Modal, Notice, TabBar, TabPanel } from './patterns';
import type { DataTableColumn } from './patterns';
import './OrchardManager.css';

interface Orchard {
  _id: string;
  orchardName: string;
  farmName: string;
  location?: {
    type: 'Point';
    coordinates: number[]; // [long, lat]
  };
  boundary?: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  cropType?: string;
  cultivar?: string;
  variety?: string;
  rootstock?: string;
  numberOfTrees?: number;
  areaHectares?: number;
  yearOfPlanting?: number;
  spacingX?: number;
  spacingY?: number;
  soilType?: 'Clay' | 'Sandy' | 'Loam';
  irrigationType?: 'Drip' | 'Micro' | 'Surface';
  nets?: 'Full' | 'Individual' | 'None';
  activePetalFallDate?: string | null;
}

interface OrchardManagerProps {
  basePath?: string;
}

const OrchardManager: React.FC<OrchardManagerProps> = ({ basePath = '/orchards' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Determine active tab from URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.endsWith('/map')) return 'map';
    if (path.endsWith('/import')) return 'import';
    if (path.endsWith('/migrate')) return 'migrate';
    return 'list';
  };

  const handleTabSelect = (key: string | null) => {
    if (key === 'list') {
      navigate(basePath);
    } else {
      // Ensure we don't double slash if basePath ends with /
      const cleanBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
      navigate(`${cleanBasePath}/${key}`);
    }
  };

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentOrchard, setCurrentOrchard] = useState<Partial<Orchard>>({
    orchardName: '',
    farmName: '',
    soilType: 'Loam',
    irrigationType: 'Drip',
    nets: 'None'
  });

  const [cropTypes, setCropTypes] = useState<CropTypeNode[]>([]);
  const [lat, setLat] = useState<string>('');
  const [long, setLong] = useState<string>('');

  const mockToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InVzZXIxIiwidGVuYW50SWQiOiJ0ZW5hbnQxMjMiLCJpYXQiOjE3NzA0NTIyMTF9.p0QS4h5rJY4PWFboy8vBzSCjo3dW-awd8c4aPwKKq_Q';
  const apiConfig = {
    headers: { 'Authorization': `Bearer ${mockToken}` }
  };

  useEffect(() => {
    try {
      const decoded: any = jwtDecode(mockToken);
      setUserRole(decoded.role || 'Farm Manager'); // Fallback for mock if role missing
    } catch (e) {
      console.error('Failed to decode token');
    }
    fetchOrchards();
    // fetchOrchards is defined below; stable at runtime (arrow fn referencing constant token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isManager = userRole === 'Farm Manager';

  const fetchOrchards = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/v1/orchards', apiConfig);
      setOrchards(response.data);
    } catch (err: any) {
      console.warn('Failed to fetch orchards from API, falling back to local storage:', err);
      try {
        const localOrchards = await db.orchards.toArray();
        if (localOrchards.length > 0) {
          // Map Dexie Orchard to component Orchard interface (id vs _id)
          setOrchards(localOrchards.map(o => ({
            ...o,
            _id: o.id
          })) as any);
          setError(t('orchardManager.offlineCache'));
        } else {
          setError(err.response?.data?.error || 'Failed to fetch orchards and no local data available.');
        }
      } catch (dbErr) {
        console.error('Failed to fetch from local DB:', dbErr);
        setError(err.response?.data?.error || 'Failed to fetch orchards');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrchards();
    // fetchOrchards is defined above; stable at runtime (arrow fn referencing constant token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listGlobalCropTypes().then(setCropTypes).catch(() => setCropTypes([]));
  }, []);

  const handleOpenModal = (orchard?: Orchard) => {
    if (orchard) {
      setIsEditing(true);
      setCurrentOrchard(orchard);
      setLat(orchard.location?.coordinates[1]?.toString() || '');
      setLong(orchard.location?.coordinates[0]?.toString() || '');
    } else {
      setIsEditing(false);
      setCurrentOrchard({
        orchardName: '',
        farmName: '',
        cropType: 'citrus',
        soilType: 'Loam',
        irrigationType: 'Drip',
        nets: 'None'
      });
      setLat('');
      setLong('');
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | any>) => {
    const { name, value } = e.target;
    setCurrentOrchard(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload = { ...currentOrchard };
    if (lat && long) {
      payload.location = {
        type: 'Point',
        coordinates: [parseFloat(long), parseFloat(lat)]
      };
    }

    try {
      if (isEditing && currentOrchard._id) {
        await axios.put(`/api/v1/orchards/${currentOrchard._id}`, payload, apiConfig);
      } else {
        await axios.post('/api/v1/orchards', payload, apiConfig);
      }
      await syncOrchards();
      await fetchOrchards();
      handleCloseModal();
      setSuccess(t('orchardManager.orchardSaved'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save orchard');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('orchardManager.deleteConfirm'))) return;

    setLoading(true);
    try {
      await axios.delete(`/api/v1/orchards/${id}`, apiConfig);
      await syncOrchards();
      await fetchOrchards();
      setSuccess(t('orchardManager.orchardDeleted'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete orchard');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'kml' | 'csv') => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) return;

    setLoading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = type === 'kml' ? '/api/v1/orchards/upload-kml' : '/api/v1/orchards/upload-csv';
      const response = await axios.post(endpoint, formData, {
        headers: {
          ...apiConfig.headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      await syncOrchards();
      setSuccess(t('orchardManager.uploadComplete', { count: response.data.processed }));
      await fetchOrchards();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to upload ${type.toUpperCase()}`);
    } finally {
      setLoading(false);
      // Clear input
      e.target.value = '';
    }
  };

  const orchardColumns: DataTableColumn<Orchard>[] = [
    {
      key: 'orchardName',
      label: t('orchardManager.orchardHeader'),
      render: (o) => <strong>{o.orchardName}</strong>,
    },
    {
      key: 'farmName',
      label: t('orchardManager.farmHeader'),
      render: (o) => o.farmName,
    },
    {
      key: 'cultivarVariety',
      label: t('orchardManager.cultivarVariety'),
      render: (o) => `${o.cultivar ?? ''} ${o.variety ? `(${o.variety})` : ''}`.trim(),
    },
    {
      key: 'soilIrrigation',
      label: t('orchardManager.soilIrrigation'),
      render: (o) => `${o.soilType ?? ''} / ${o.irrigationType ?? ''}`,
    },
    {
      key: 'petalFallDate',
      label: t('orchardManager.petalFallDate'),
      render: (o) => o.activePetalFallDate ? new Date(o.activePetalFallDate).toISOString().split('T')[0] : '—',
    },
    {
      key: 'actions',
      label: t('orchardManager.actionsHeader'),
      render: (o) => (
        isManager ? (
          <>
            <button
              type="button"
              className="pat-btn pat-btn--subtle me-2"
              onClick={() => handleOpenModal(o)}
            >
              {t('orchardManager.edit')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--danger"
              onClick={() => handleDelete(o._id)}
            >
              {t('orchardManager.delete')}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="pat-btn pat-btn--outline"
            onClick={() => handleOpenModal(o)}
          >
            {t('orchardManager.viewDetails')}
          </button>
        )
      ),
    },
  ];

  return (
    <div className="orchard-manager">
      <div className="orchard-manager__header">
        <h2 className="orchard-manager__heading">{t('orchardManager.heading')}</h2>
        {isManager && (
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={() => handleOpenModal()}
          >
            {t('orchardManager.addNew')}
          </button>
        )}
      </div>

      {error && (
        <Notice tone="danger" className="mb-3" action={<button type="button" className="pat-btn pat-btn--subtle" onClick={() => setError(null)} aria-label="Close">×</button>}>
          <span>{error}</span>
        </Notice>
      )}
      {success && (
        <Notice tone="success" role="alert" className="mb-3" action={<button type="button" className="pat-btn pat-btn--subtle" onClick={() => setSuccess(null)} aria-label="Close">×</button>}>
          <span>{success}</span>
        </Notice>
      )}

      <div className="orchard-manager__tabs">
        <TabBar activeKey={getActiveTab()} onSelect={handleTabSelect}>
          <TabPanel id="list" title={t('orchardManager.listTab')}>
            <DataTable<Orchard>
              columns={orchardColumns}
              rows={orchards}
              rowKey={(o) => o._id}
              emptyState={<div className="text-center py-3">{t('orchardManager.noOrchards')}</div>}
            />
          </TabPanel>
          <TabPanel id="map" title={t('orchardManager.mapTab')}>
            <div className="orchard-manager__map-wrap">
              <OrchardMap orchards={orchards} onOrchardClick={handleOpenModal} />
            </div>
          </TabPanel>
          {isManager && (
            <TabPanel id="import" title={t('orchardManager.importTab')}>
              <div className="orchard-manager__grid-2">
                <FormSection
                  id="orchard-kml-upload"
                  title={t('orchardManager.kmlTitle')}
                  description={t('orchardManager.kmlText')}
                >
                  <Field label={t('orchardManager.selectKml')} controlId="kmlFile">
                    <input
                      id="kmlFile"
                      type="file"
                      accept=".kml"
                      onChange={(e: any) => handleFileUpload(e, 'kml')}
                      disabled={loading}
                    />
                  </Field>
                </FormSection>
                <FormSection
                  id="orchard-csv-upload"
                  title={t('orchardManager.csvTitle')}
                  description={t('orchardManager.csvText')}
                >
                  <Field label={t('orchardManager.selectCsv')} controlId="csvFile">
                    <input
                      id="csvFile"
                      type="file"
                      accept=".csv"
                      onChange={(e: any) => handleFileUpload(e, 'csv')}
                      disabled={loading}
                    />
                  </Field>
                </FormSection>
              </div>
            </TabPanel>
          )}
          <TabPanel id="migrate" title={t('orchardManager.migrateTab')}>
            <LegacyMigrationDashboard />
          </TabPanel>
        </TabBar>
      </div>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        title={isEditing ? t('orchardManager.editTitle') : t('orchardManager.addTitle')}
      >
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <fieldset disabled={!isManager} className="orchard-manager__fieldset">
              <div className="orchard-manager__grid-2">
                <Field label={t('orchardManager.orchardName')} controlId="orchardName" required>
                  <input
                    id="orchardName"
                    name="orchardName"
                    value={currentOrchard.orchardName || ''}
                    onChange={handleChange}
                    required
                    placeholder={t('orchardManager.orchardNamePlaceholder')}
                  />
                </Field>
                <Field label={t('orchardManager.farmName')} controlId="farmName" required>
                  <input
                    id="farmName"
                    name="farmName"
                    value={currentOrchard.farmName || ''}
                    onChange={handleChange}
                    required
                    placeholder={t('orchardManager.farmNamePlaceholder')}
                  />
                </Field>
              </div>

              <div className="orchard-manager__grid-2">
                <Field label={t('orchardManager.latitude')} controlId="lat">
                  <input
                    id="lat"
                    type="number"
                    step="any"
                    value={lat}
                    onChange={(e) => setLat(e.target.value)}
                    placeholder="-33.93"
                  />
                </Field>
                <Field label={t('orchardManager.longitude')} controlId="long">
                  <input
                    id="long"
                    type="number"
                    step="any"
                    value={long}
                    onChange={(e) => setLong(e.target.value)}
                    placeholder="18.84"
                  />
                </Field>
              </div>

              <div className="orchard-manager__grid-2">
                <Field label={t('orchardManager.cropType')} controlId="cropType">
                  <select
                    id="cropType"
                    name="cropType"
                    className="pat-input"
                    value={currentOrchard.cropType || 'citrus'}
                    onChange={handleChange}
                    aria-label={t('orchardManager.cropType')}
                  >
                    {cropTypes.length === 0 && <option value="citrus">citrus</option>}
                    {cropTypes.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="orchard-manager__grid-3">
                <Field label={t('orchardManager.cultivar')} controlId="cultivar">
                  <input
                    id="cultivar"
                    name="cultivar"
                    value={currentOrchard.cultivar || ''}
                    onChange={handleChange}
                  />
                </Field>
                <Field label={t('orchardManager.variety')} controlId="variety">
                  <input
                    id="variety"
                    name="variety"
                    value={currentOrchard.variety || ''}
                    onChange={handleChange}
                  />
                </Field>
                <Field label={t('orchardManager.rootstock')} controlId="rootstock">
                  <input
                    id="rootstock"
                    name="rootstock"
                    value={currentOrchard.rootstock || ''}
                    onChange={handleChange}
                  />
                </Field>
              </div>

              <div className="orchard-manager__grid-3">
                <Field label={t('orchardManager.trees')} controlId="trees">
                  <input
                    id="trees"
                    type="number"
                    name="numberOfTrees"
                    value={currentOrchard.numberOfTrees || ''}
                    onChange={handleChange}
                  />
                </Field>
                <Field label={t('orchardManager.area')} controlId="area">
                  <input
                    id="area"
                    type="number"
                    step="0.01"
                    name="areaHectares"
                    value={currentOrchard.areaHectares || ''}
                    onChange={handleChange}
                  />
                </Field>
                <Field label={t('orchardManager.yearPlanted')} controlId="year">
                  <input
                    id="year"
                    type="number"
                    name="yearOfPlanting"
                    value={currentOrchard.yearOfPlanting || ''}
                    onChange={handleChange}
                  />
                </Field>
              </div>

              <div className="orchard-manager__grid-3">
                <Field label={t('orchardManager.soilType')} controlId="soilType">
                  <select
                    id="soilType"
                    name="soilType"
                    value={currentOrchard.soilType}
                    onChange={handleChange}
                  >
                    <option value="Clay">Clay</option>
                    <option value="Sandy">Sandy</option>
                    <option value="Loam">Loam</option>
                  </select>
                </Field>
                <Field label={t('orchardManager.irrigation')} controlId="irrigationType">
                  <select
                    id="irrigationType"
                    name="irrigationType"
                    value={currentOrchard.irrigationType}
                    onChange={handleChange}
                  >
                    <option value="Drip">Drip</option>
                    <option value="Micro">Micro</option>
                    <option value="Surface">Surface</option>
                  </select>
                </Field>
                <Field label={t('orchardManager.nets')} controlId="nets">
                  <select
                    id="nets"
                    name="nets"
                    value={currentOrchard.nets}
                    onChange={handleChange}
                  >
                    <option value="None">None</option>
                    <option value="Full">Full</option>
                    <option value="Individual">Individual</option>
                  </select>
                </Field>
              </div>
            </fieldset>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={handleCloseModal}
            >
              {isManager ? t('orchardManager.cancel') : t('orchardManager.close')}
            </button>
            {isManager && (
              <button
                type="submit"
                className="pat-btn pat-btn--primary"
                disabled={loading}
              >
                {isEditing ? t('orchardManager.update') : t('orchardManager.save')}
              </button>
            )}
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default OrchardManager;
